// ═══════════════════════════════════════════════════════════════════════════
// Dynamic-block AI → EditorCommand translators (CREATE_DYNAMIC_BLOCK,
// SET_VISIBILITY_STATE, LOOKUP_SELECT, UPDATE_DYNAMIC_PARAMETER,
// SET_CANNOSCALE).
// ═══════════════════════════════════════════════════════════════════════════

import type { EditorCommand } from "@dxf-ai-editor/contracts";

import type {
  CreateDynamicBlockAiCommand,
  LookupSelectAiCommand,
  SetCannoscaleAiCommand,
  SetVisibilityStateAiCommand,
  UpdateDynamicParameterAiCommand,
} from "./blockSchemas";
import {
  AI_COMMAND_SOURCE,
  type AiDispatchContext,
} from "./blockDispatcherShared";

// ─── CREATE_DYNAMIC_BLOCK ─────────────────────────────────────────
//
// Fan-out:
//   1. `geometry/create-block-definition`  — baseline block
//   2. `space/enter-block-edit`            — open authoring context
//   3. `geometry/add-dynamic-parameter`    — one per authored parameter
//   4. `geometry/add-dynamic-action`       — one per authored action
//   5. `geometry/create-lookup-table`      — if any tables are authored
//   6. `space/exit-block-edit action=save` — commit
export function translateCreateDynamicBlock(
  command: CreateDynamicBlockAiCommand,
): EditorCommand[] {
  const commands: EditorCommand[] = [];
  commands.push({
    type: "geometry/create-block-definition",
    source: AI_COMMAND_SOURCE,
    blockDef: {
      name: command.name,
      handle: "",
      layer: "0",
      flags: 0,
      basePoint: {
        x: command.basePoint.x,
        y: command.basePoint.y,
        z: 0,
      },
      xrefPathName: "",
      description: command.description,
      entities: command.entities,
    },
    blockRecord: {
      name: command.name,
      insertionUnits: command.insertionUnits,
      explodable: command.explodable,
      scalable: command.scalable,
      isAnnotative: false,
      description: command.description,
    },
    postAction: "retain",
    sourceEntityIds: [],
  });
  commands.push({
    type: "space/enter-block-edit",
    source: AI_COMMAND_SOURCE,
    blockName: command.name,
    createIfMissing: false,
  });
  for (const parameter of command.parameters) {
    commands.push({
      type: "geometry/add-dynamic-parameter",
      source: AI_COMMAND_SOURCE,
      blockName: command.name,
      parameter,
    });
  }
  for (const action of command.actions) {
    commands.push({
      type: "geometry/add-dynamic-action",
      source: AI_COMMAND_SOURCE,
      blockName: command.name,
      action,
    });
  }
  for (const table of command.lookupTables) {
    commands.push({
      type: "geometry/create-lookup-table",
      source: AI_COMMAND_SOURCE,
      blockName: command.name,
      table,
    });
  }
  commands.push({
    type: "space/exit-block-edit",
    source: AI_COMMAND_SOURCE,
    action: "save",
  });
  return commands;
}

// ─── SET_VISIBILITY_STATE ─────────────────────────────────────────
//
// Sets the per-INSERT visibility state override. The internal
// `properties/apply` pipeline is used (matches the Properties palette
// dropdown UI) — we route through a stable `Visibility:<paramName>`
// key the reducer's property translator maps onto the right parameter.
export function translateSetVisibilityState(
  command: SetVisibilityStateAiCommand,
  _context: AiDispatchContext,
): EditorCommand[] {
  const propertyKey = `Visibility:${command.parameterName}`;
  return [
    {
      type: "properties/apply",
      source: AI_COMMAND_SOURCE,
      entityIds: [command.insertId],
      values: {
        [propertyKey]: command.stateName,
      },
    },
  ];
}

// ─── LOOKUP_SELECT ────────────────────────────────────────────────
//
// AutoCAD's reverse-lookup behaviour — pick an output value and the
// cascade updates input parameters. When the context supplies a
// resolver we expand to per-parameter UPDATE commands. Otherwise we
// emit a single chprop/apply against a synthetic key the reducer
// interprets as "lookup output = <value>" and resolves internally.
export function translateLookupSelect(
  command: LookupSelectAiCommand,
  context: AiDispatchContext,
): EditorCommand[] {
  const resolver = context.resolveLookupOutput;
  if (resolver) {
    const resolved = resolver({
      insertId: command.insertId,
      parameterName: command.parameterName,
      outputValue: command.outputValue,
    });
    if (resolved && resolved.length > 0) {
      return resolved.map<EditorCommand>((update) => ({
        type: "chprop/apply",
        source: AI_COMMAND_SOURCE,
        entityIds: [command.insertId],
        property: `Parameter:${update.parameterName}`,
        value: String(update.value),
      }));
    }
  }
  return [
    {
      type: "chprop/apply",
      source: AI_COMMAND_SOURCE,
      entityIds: [command.insertId],
      property: `LookupOutput:${command.parameterName}`,
      value: String(command.outputValue),
    },
  ];
}

// ─── UPDATE_DYNAMIC_PARAMETER ─────────────────────────────────────
export function translateUpdateDynamicParameter(
  command: UpdateDynamicParameterAiCommand,
): EditorCommand[] {
  return [
    {
      type: "chprop/apply",
      source: AI_COMMAND_SOURCE,
      entityIds: [command.insertId],
      property: `Parameter:${command.parameterName}`,
      value: String(command.value),
    },
  ];
}

// ─── SET_CANNOSCALE ───────────────────────────────────────────────
export function translateSetCannoscale(
  command: SetCannoscaleAiCommand,
): EditorCommand[] {
  return [
    {
      type: "header/set-cannoscale",
      source: AI_COMMAND_SOURCE,
      cannoscaleName: command.cannoscaleName,
    },
  ];
}
